import React from 'react';
import { WidgetWrapper, useWidgetEditable } from '@brandclub/bc-widgets';
import {
  SUPPORT_TICKET_CARD_WIDGET_ID,
  SUPPORT_TICKET_CARD_DISPLAY_NAME,
  supportTicketCardDefaultState,
  supportTicketCardEditFields,
  buildSupportTicketCardStyles,
  type SupportTicketCardState,
  type SupportTicketCardStyles,
} from './supportTicketCardConfig';

/**
 * Shared edit chrome for the three support-ticket cards. Registers the family's
 * fields with the theme editor and renders the pencil via `WidgetWrapper`;
 * outside edit mode `WidgetWrapper` returns its children untouched, so the
 * production DOM is unchanged.
 *
 * All three cards share one widget id, so editing any of them restyles the
 * whole family.
 */
export function SupportTicketCardChrome({
  children,
}: {
  children: (styles: SupportTicketCardStyles) => React.ReactNode;
}) {
  const {
    state,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    studioLockedKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<SupportTicketCardState>(
    SUPPORT_TICKET_CARD_WIDGET_ID,
    supportTicketCardDefaultState,
    supportTicketCardEditFields,
  );

  const styles = buildSupportTicketCardStyles(state, resolveColor);

  return (
    <WidgetWrapper
      widgetId={SUPPORT_TICKET_CARD_WIDGET_ID}
      displayName={SUPPORT_TICKET_CARD_DISPLAY_NAME}
      editFields={supportTicketCardEditFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      studioLockedKeys={studioLockedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      {children(styles)}
    </WidgetWrapper>
  );
}
